import { SidebarProvider } from "@/components/ui/sidebar";
import { DocumentsContainer } from "@/features/documents/DocumentsContainer";
import { AppSidebar } from "@/features/shell/AppSidebar";
import { useAppState } from "@/store/useApp";
import { Course } from "@/types/course";
import OnboardingAnnouncementFlow from "./reader/ onboarding/flows/OnboardingAnnouncementFlow";
import OnboardingSurveyFlow from "./reader/ onboarding/flows/OnboardingSurveyFlow";
import OnboardingDemoAssignmentHintFlow from "./reader/ onboarding/flows/OnboardingDemoAssignmentHintFlow";
import { ErrorAlert } from "@/components/ErrorPanel";
import { useAuth } from "@/hooks/use-auth";
import { CoursePage } from "./courses/editor/CoursePage";
import { Agents } from "./agents/Agents";
import { AgentEditor } from "./agents/AgentEditor";
import { Audits } from "./audits/Audits";
import { Insights } from "./insights/Insights";
import { Routes, Route, Navigate, useParams } from "react-router";
import { DocumentView } from "@/types/document";

// Wrapper component to get courseId from URL params
const CourseAssessmentsRoute = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const courses = useAppState((state) => state.courses);
  const course = courses?.find((c: Course) => c.id === courseId);

  if (!course) {
    return <Navigate to="/inbox/ready" replace />;
  }

  return <CoursePage course={course} />;
};

// Wrapper component to get view from URL params
const InboxRoute = () => {
  const { view } = useParams<{ view: string }>();
  const validView = view as DocumentView;

  return <DocumentsContainer currentView={validView} />;
};

export const Home = () => {
  const appError = useAppState((state) => state.error);
  const { logout } = useAuth();

  // Handle auth errors by logging out immediately
  if (appError?.type === "auth") {
    logout();
    return null;
  }

  return (
    <SidebarProvider defaultOpen>
      <div className="w-full h-screen flex">
        <AppSidebar />
    
        <OnboardingDemoAssignmentHintFlow />
        <OnboardingAnnouncementFlow />
        <OnboardingSurveyFlow />

        <div className="flex-1 min-w-0">
          <Routes>
            {/* Inbox routes */}
            <Route path="/inbox/:view" element={<InboxRoute />} />

            {/* Course routes */}
            <Route
              path="/courses/:courseId"
              element={<CourseAssessmentsRoute />}
            />

            {/* Agent routes */}
            <Route path="/insights" element={<Insights />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/:agentId" element={<AgentEditor />} />
            <Route path="/audits" element={<Audits />} />

            {/* Default redirect to inbox/ready */}
            <Route path="/" element={<Navigate to="/inbox/ready" replace />} />
            <Route path="*" element={<Navigate to="/inbox/ready" replace />} />
          </Routes>
        </div>
      </div>

      {/* Show error toasts for non-auth errors */}
      {appError &&
        (appError.type === "network" || appError.type === "other") && (
          <ErrorAlert error={appError} />
        )}
    </SidebarProvider>
  );
};
